import { useState } from "react";
import axios from "axios";
import { Modal } from "@/components/kit";

const pub = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
const TYPE = { resident: "Resident", nextgen_youth: "Next Gen Youth", business: "Business", organization: "Organization", church: "Church" };

// "Checking in as" — every role pre-ticked; one tap records attendance for each ticked role.
export function RolePicker({ data, onDone, onCancel }) {
  const [ids, setIds] = useState(data.roles.map((r) => r.member_id));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const toggle = (id) => setIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const submit = async () => {
    if (!ids.length) { setErr("Tick at least one role."); return; }
    setBusy(true); setErr("");
    try { const r = await pub.post("/checkin/roles", { ticket: data.ticket, member_ids: ids }); onDone(r.data); }
    catch (e) { setErr(e.response?.data?.detail || "Could not check in."); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="role-picker">
      <div>
        <div className="text-[13px] uppercase tracking-widest font-bold text-dark-olive">Checking in as</div>
        <div className="font-zilla text-2xl font-semibold" data-testid="role-picker-name">{data.checking_in_as}</div>
      </div>
      {data.roles.map((r) => (
        <label key={r.member_id} data-testid={`role-option-${r.member_id}`} className="flex items-center gap-3 bg-white border border-ink/20 rounded-xl px-4 py-3 min-h-[56px] cursor-pointer">
          <input type="checkbox" className="h-5 w-5 accent-maroon" checked={ids.includes(r.member_id)} onChange={() => toggle(r.member_id)} data-testid={`role-check-${r.member_id}`} />
          <span className="flex-1 min-w-0">
            <span className="block text-[16px] font-semibold truncate">{r.name}</span>
            <span className="block text-[13px] text-ink/60">{TYPE[r.account_type] || r.account_type} · {r.member_number}</span>
          </span>
        </label>
      ))}
      {err && <div className="text-maroon text-[15px]" data-testid="role-picker-error">{err}</div>}
      <button data-testid="role-picker-submit" disabled={busy} onClick={submit} className="w-full rounded-xl px-5 py-4 min-h-[60px] text-[18px] font-semibold bg-maroon text-cream disabled:opacity-50">
        {busy ? "Checking in…" : "Check in"}
      </button>
      {onCancel && <button data-testid="role-picker-cancel" onClick={onCancel} className="w-full text-dark-olive font-semibold text-[15px] py-2">Cancel</button>}
    </div>
  );
}

export function RolePickerModal({ data, onDone, onClose }) {
  return (
    <Modal open={!!data} onClose={onClose} title="Confirm check-in" data-testid="role-picker-modal">
      {data && <RolePicker data={data} onDone={onDone} />}
    </Modal>
  );
}

// Guest from a business with no login: search the business, type the guest's name.
export function GuestForm({ onSuccess }) {
  const [q, setQ] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [org, setOrg] = useState(null);
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const search = async (v) => {
    setQ(v); setOrg(null);
    if (v.trim().length < 3) { setOrgs([]); return; }
    try { const r = await pub.get("/checkin/orgs", { params: { q: v } }); setOrgs(r.data.orgs); } catch { setOrgs([]); }
  };
  const submit = async () => {
    setErr("");
    try { const r = await pub.post("/checkin/guest", { org_id: org?.member_id, guest_name: name }); onSuccess({ name: r.data.name, already: r.data.already }); }
    catch (e) { setErr(e.response?.data?.detail || "Could not check in."); }
  };
  const box = "w-full bg-white border border-ink/20 rounded-xl px-4 py-3 min-h-[52px] text-[17px]";
  return (
    <div className="space-y-3" data-testid="guest-form">
      <input data-testid="guest-org-search" value={org ? org.name : q} onChange={(e) => search(e.target.value)} placeholder="Business or organization name" className={box} />
      {!org && orgs.map((o) => (
        <button key={o.member_id} data-testid={`guest-org-${o.member_id}`} onClick={() => setOrg(o)} className="w-full text-left bg-cream border border-ink/15 rounded-xl px-4 py-3 text-[16px] font-semibold">{o.name}</button>
      ))}
      <input data-testid="guest-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Guest's name" className={box} />
      {err && <div className="text-maroon text-[15px]" data-testid="guest-error">{err}</div>}
      <button data-testid="guest-submit" disabled={!org} onClick={submit} className="w-full rounded-xl px-5 py-4 min-h-[64px] text-[18px] font-semibold bg-dark-olive text-cream disabled:opacity-50">Check in guest</button>
    </div>
  );
}
